import { twApi } from '../api';
import { SearchParams } from '..';
import {
  Attachment,
  DeleteDocumentAttachmentResponse,
  DocumentAttachmentFilterParams,
  GetDocumentAttachmentByIdResponse,
  IGetDocumentAttachmentByIdResponse,
  ISearchDocumentAttachmentsResponse,
  MarkAsReviewedDocumentAttachmentParams,
  MarkAsReviewedDocumentAttachmentResponse,
  SearchDocumentAttachmentResponse,
  UpdateDocumentAttachmentParams,
  UploadDocumentAttachmentParams,
  UploadDocumentAttachmentResponse
} from './documentAttachment.model';

const attachmentsApi = twApi.injectEndpoints({
  endpoints: (builder) => ({
    searchDocumentAttachments: builder.query<
      ISearchDocumentAttachmentsResponse,
      SearchParams<DocumentAttachmentFilterParams>
    >({
      query: ({ filters, params }) => ({
        url: '/document-attachments/search',
        params: { ...params },
        method: 'POST',
        body: { ...filters }
      }),
      transformResponse: (response: SearchDocumentAttachmentResponse) => {
        if (!response.success) {
          throw new Error('Failed to fetch document attachments');
        }

        return {
          attachments: response.data,
          total: response.total
        };
      },
      providesTags: (result) =>
        result?.attachments
          ? [
              ...result.attachments.map(({ id }) => ({ type: 'Attachments' as const, id })),
              { type: 'Attachments', id: 'ATTACHMENTS_LIST' }
            ]
          : [{ type: 'Attachments', id: 'ATTACHMENTS_LIST' }]
    }),
    getDocumentAttachmentById: builder.query<IGetDocumentAttachmentByIdResponse, { id: string }>({
      query: ({ id }) => ({
        url: `/document-attachments/${id}`,
        method: 'GET'
      }),
      transformResponse: (response: GetDocumentAttachmentByIdResponse) => {
        return { attachment: response.data };
      },
      providesTags: (result) =>
        result?.attachment ? [{ type: 'Attachments' as const, id: result.attachment.id }] : []
    }),
    markAsReviewedBulk: builder.mutation<
      { attachments: Attachment[] },
      MarkAsReviewedDocumentAttachmentParams
    >({
      query: (params) => {
        const { filters, reviewed } = params;
        return {
          url: '/document-attachments/mark-as-reviewed',
          method: 'POST',
          body: { filters, reviewed }
        };
      },
      transformResponse: (response: MarkAsReviewedDocumentAttachmentResponse) => {
        if (!response.success) {
          throw new Error('Failed to mark attachments as reviewed');
        }

        return { attachments: response.data };
      },
      invalidatesTags: () => [{ type: 'Attachments', id: 'ATTACHMENTS_LIST' }]
    }),
    uploadDocumentAttachment: builder.mutation<
      UploadDocumentAttachmentResponse,
      UploadDocumentAttachmentParams
    >({
      query: (params) => {
        const formData = new FormData();

        formData.append('attachment', params.attachment);
        formData.append('type', params.type || 'EMPTY');
        formData.append('visibility', params.visibility ? 'true' : 'false');
        params.orderId && formData.append('order_id', params.orderId);
        params.tripId && formData.append('trip_id', params.tripId);
        params.expenseId && formData.append('expense_id', params.expenseId);
        params.driverId && formData.append('driver_id', params.driverId);
        params.equipmentId && formData.append('equipment_id', params.equipmentId);

        return {
          url: '/document-attachments/upload',
          method: 'POST',
          body: formData,
          formData: true
        };
      },
      invalidatesTags: () => [{ type: 'Attachments', id: 'ATTACHMENTS_LIST' }]
    }),
    updateDocumentAttachment: builder.mutation<Attachment, UpdateDocumentAttachmentParams>({
      query: (params) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { previousValues, ...payload } = params;
        return {
          url: `/document-attachments/${params.id}`,
          method: 'PATCH',
          body: { ...payload }
        };
      },
      invalidatesTags: (result, error, data) => {
        const otherTags: any[] = [];

        if (result) {
          if (data.expense_id) otherTags.push({ type: 'OtherExpenses', id: data.expense_id });
          if (data.order_id) otherTags.push({ type: 'Orders', id: data.order_id });
          if (data.trip_id) otherTags.push({ type: 'Trips', id: data.trip_id });

          if (data.previousValues) {
            if (data.previousValues.expense_id)
              otherTags.push({ type: 'OtherExpenses', id: data.previousValues.expense_id });
            if (data.previousValues.order_id)
              otherTags.push({ type: 'Orders', id: data.previousValues.order_id });
            if (data.previousValues.trip_id)
              otherTags.push({ type: 'Trips', id: data.previousValues.trip_id });
          }
        }

        return [
          { type: 'Attachments', id: data.id },
          { type: 'Attachments', id: 'ATTACHMENTS_LIST' },
          { type: 'OtherExpenses', id: 'OTHER_EXPENSES_LIST' },
          { type: 'Orders', id: 'ORDERS_LIST' },
          { type: 'Trips', id: 'TRIPS_LIST' },
          { type: 'ActiveTrips', id: 'TRIPS_LIST' },
          ...otherTags
        ];
      }
    }),
    deleteDocumentAttachment: builder.mutation<{ message: string }, { id: string }>({
      query: ({ id }) => ({
        url: `/document-attachments/${id}`,
        method: 'DELETE'
      }),
      transformResponse: (response: DeleteDocumentAttachmentResponse) => {
        if (!response.success) {
          throw new Error('Failed to delete attachment');
        }

        return { message: response.message };
      },
      invalidatesTags: (_, __, { id }) => [
        { type: 'Attachments', id },
        { type: 'Attachments', id: 'ATTACHMENTS_LIST' }
      ]
    })
  })
});

export const {
  useSearchDocumentAttachmentsQuery,
  useGetDocumentAttachmentByIdQuery,
  useMarkAsReviewedBulkMutation,
  useUploadDocumentAttachmentMutation,
  useUpdateDocumentAttachmentMutation,
  useDeleteDocumentAttachmentMutation
} = attachmentsApi;
